import React, { useState, useEffect } from 'react';

import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { Form, Modal, Select, Input, InputNumber, Flex, Button, Space, DatePicker } from 'antd';
import { NotificationInstance } from 'antd/lib/notification/interface';
import { PlusOutlined } from '@ant-design/icons';

import { useAppDispatch } from '@store/hooks';

import Account from '@controls/account';
import IBaseComponentProps from '@components/base-component-props';

import { getEnumList, toFinanceString } from '@extensions/utils';
import { warning, exception } from '@extensions/notification';
import { userLoaded } from '@store/actions';

import { IUserAccount } from '@entities/user-account';
import { IUser } from '@entities/user';
import { ISystemSetting } from '@entities/system-setting';
import { ITransaction } from '@entities/transaction';
import { ITransactionParams } from '@entities/transaction-params';

import { IEnumItem } from '@enums/enum-item';
import { Currency, enumSign as currencySign } from '@enums/currency';
import { ExchangeRateType, enumLabel as rateTypeLabel } from '@enums/exchange-rate-type';
import { ExchangeRateCommissionType } from '@enums/exchange-rate-commission-type';
import { SystemSettingType } from '@enums/system-setting-type';
import { UserAccountType } from '@enums/user-account-type';
import { UserBalanceType } from '@enums/user-balance-type';

dayjs.extend(utc);

interface ITransferProps extends IBaseComponentProps {
    userId: string;
    userLogin: string;
    accounts: Array<IUserAccount>;
    transaction?: ITransaction;
    userBalanceType?: UserBalanceType;
    onSave: () => void;
    onCancel: () => void;
    api: NotificationInstance;
}

const TransferAccountsModal = (props: ITransferProps) => {
    const { userLogin, userId, accounts, transaction, userBalanceType, api, onSave, onCancel, serverFetch } = props;

    const d = useAppDispatch();

    const [form] = Form.useForm();

    const amount: number = Form.useWatch('amount', { form });
    const exchangeRate: number = Form.useWatch('exchangeRate', { form });
    const exchangeRateDate: Dayjs | undefined = Form.useWatch('exchangeRateDate', { form });
    const exchangeRateCommission: number | undefined = Form.useWatch('exchangeRateCommission', { form });
    const exchangeRateCommissionType: ExchangeRateCommissionType = Form.useWatch('exchangeRateCommissionType', { form });

    const [currentFromUserAccount, setCurrentFromUserAccount] = useState<IUserAccount>();
    const [currentUserAccount, setCurrentUserAccount] = useState<IUserAccount>();
    const [rateTypes] = useState<Array<IEnumItem>>(getEnumList(ExchangeRateType, rateTypeLabel));
    const [rateCommissionTypes] = useState<Array<IEnumItem>>(getEnumList(ExchangeRateCommissionType));

    const [fromAccounts] = useState<Array<IUserAccount>>(accounts.filter((a) => a.type === UserAccountType.User));
    const [toAccounts] = useState<Array<IUserAccount>>(accounts.filter((a) => a.type !== UserAccountType.User));

    const [internalUsdExchangeRate, setInternalUsdExchangeRate] = useState<number | undefined>();

    const [currentRateType, setCurrentRateType] = useState<ExchangeRateType | undefined>(ExchangeRateType.CentralBank);

    const [user, setUser] = useState<IUser>();

    const [refreshRequired, setRefreshRequired] = useState<boolean>(true);
    const [processing, setProcessing] = useState<boolean>(false);

    useEffect(() => {
        let cleanup = false;

        if (!refreshRequired) return;

        const fetchData = async () => {
            const promises = [
                await serverFetch(`users/${userId}`, { method: 'GET' })
                    .then((data: any) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения пользователя', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch(`systemSettings/${SystemSettingType.InternalExchangeRate}`, { method: 'GET' })
                    .then((data: any) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения внутреннего курса', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                setUser(result[0][0]);

                const internalUsdExchangeRateSetting: ISystemSetting = result[0][1];
                setInternalUsdExchangeRate(
                    internalUsdExchangeRateSetting && internalUsdExchangeRateSetting.value ? +internalUsdExchangeRateSetting.value : 0,
                );

                if (transaction) {
                    form.setFieldValue('transactionId', transaction.id);

                    setCurrentUserAccount(accounts.find((a) => a.id == transaction.userAccountId));
                    setCurrentRateType(transaction.exchangeRateType);

                    const fromAcount = accounts.find((a) => a.id == transaction.fromUserAccountId);
                    if (fromAcount) {
                        fromAcount.amount = transaction.fromAmount;
                        setCurrentFromUserAccount(fromAcount);
                    }
                }

                setRefreshRequired(false);
            });
        };

        fetchData();

        return () => {
            cleanup = true;
        };
    }, [refreshRequired]);

    useEffect(() => {
        if (transaction) {
            form.setFieldsValue({
                userId: userId,
                exchangeRateCommissionType: transaction.exchangeRateCommissionType,
                exchangeRateCommission: transaction.exchangeRateCommission,
            });
        } else {
            if (user && user.userSettings) {
                form.setFieldsValue({
                    userId: userId,
                    exchangeRateCommissionType: user.userSettings.exchangeRateCommissionType || ExchangeRateCommissionType.Percentage,
                    exchangeRateCommission: user.userSettings.exchangeRateCommission,
                });

                if (user.userSettings.useInternalExchangeRate) {
                    setCurrentRateType(ExchangeRateType.Internal);
                }
            } else {
                form.setFieldsValue({
                    userId: userId,
                    exchangeRateCommissionType: ExchangeRateCommissionType.Percentage,
                });
            }
        }
    }, [user]);

    useEffect(() => {
        if (currentFromUserAccount?.currency == Currency.Rub && currentUserAccount?.currency == Currency.Usd) {
            if (transaction) {
                form.setFieldValue('exchangeRate', transaction.exchangeRate);
            } else if (!currentRateType || currentRateType != ExchangeRateType.CentralBank) {
                form.setFieldValue('exchangeRate', internalUsdExchangeRate);
            }
        } else {
            form.setFieldValue('exchangeRate', undefined);
        }

        form.setFieldValue(
            'exchangeRateDate',
            currentRateType == ExchangeRateType.CentralBank ? dayjs().utc(true).startOf('day') : undefined,
        );
    }, [currentFromUserAccount, currentUserAccount, currentRateType]);

    useEffect(() => {
        if (!exchangeRateDate) return;

        serverFetch(`services/cb/${exchangeRateDate}/${Currency.Usd}/exchangerate`, { method: 'GET' })
            .then((data: number | undefined) => {
                form.setFieldValue('exchangeRate', data);
            })
            .catch((ex: any) => {
                form.setFieldValue('exchangeRate', undefined);
                exception(api, 'Ошибка получения курса ЦБ для USD', ex, () => d(userLoaded(undefined)));
            });
    }, [exchangeRateDate]);

    const onTransfer = (entity: ITransactionParams) => {
        if (!entity.amount) return;

        entity.userBalanceType = userBalanceType;

        const fromAccount = accounts.find((a) => a.id == entity.fromUserAccountId);

        if (!fromAccount?.amount || fromAccount?.amount < entity.amount) {
            warning(api, 'Недостаточно средств');
            return;
        }

        setProcessing(true);

        entity.currency = fromAccount.currency;

        const url = !!transaction ? `transactions/updatetransfer` : `transactions/transfer`;
        serverFetch(url, { method: 'POST', bodyData: entity })
            .then(() => {
                onSave();
            })
            .catch((ex: any) => {
                setProcessing(false);
                const errorMessage = !!transaction ? 'Ошибка изменения операции перевода' : 'Ошибка перевода между счетами';

                exception(api, errorMessage, ex, () => d(userLoaded(undefined)));
            });
    };

    useEffect(() => {
        if (!amount) {
            form.setFieldValue('resultAmount', undefined);
            return;
        }

        if (exchangeRate) {
            let tmpExchangeRate: number = exchangeRate;
            if (exchangeRateCommission && exchangeRateCommissionType) {
                if (exchangeRateCommissionType == ExchangeRateCommissionType.Percentage) {
                    tmpExchangeRate += exchangeRate * exchangeRateCommission * 0.01;
                } else {
                    tmpExchangeRate += exchangeRateCommission;
                }
            }

            const result = amount / tmpExchangeRate;
            form.setFieldValue('resultAmount', result);
        } else {
            form.setFieldValue('resultAmount', amount);
        }
    }, [amount, exchangeRate, exchangeRateCommission, exchangeRateCommissionType]);

    return (
        <Modal
            destroyOnHidden={true}
            width={650}
            title={transaction ? `Изменение перевода между счетами "${userLogin}"` : `Перевод между счетами "${userLogin}"`}
            open={true}
            okText='ОК'
            cancelButtonProps={{ disabled: processing }}
            okButtonProps={{ loading: processing, disabled: processing }}
            onOk={() => {
                form.submit();
            }}
            onCancel={() => onCancel()}
        >
            {user && (
                <Form
                    colon={false}
                    labelCol={{ span: 6 }}
                    wrapperCol={{ span: 18 }}
                    style={{ marginTop: 20 }}
                    form={form}
                    onFinish={onTransfer}
                >
                    <Form.Item hidden name='transactionId'>
                        <Input />
                    </Form.Item>
                    <Form.Item hidden name='userId'>
                        <Input />
                    </Form.Item>
                    <Form.Item hidden name='exchangeRateCommissionType'>
                        <Input />
                    </Form.Item>

                    <Form.Item
                        name='fromUserAccountId'
                        label='Счет cписания'
                        rules={[{ required: true, message: 'Укажите счет для списания' }]}
                        initialValue={transaction?.fromUserAccountId}
                    >
                        <Select
                            disabled={!!transaction}
                            onChange={(value: string) => {
                                setCurrentFromUserAccount(fromAccounts.find((a) => a.id == value));
                                form.setFieldValue('userAccountId', undefined);
                            }}
                            options={fromAccounts.map((a) => {
                                return {
                                    value: a.id,
                                    label: (
                                        <Flex align='center' gap='small'>
                                            <Account type={a.type} currency={a.currency} userBalanceType={a.userBalanceType} />
                                            <span style={{}}>{toFinanceString(a.amount || 0, 2)}</span>
                                        </Flex>
                                    ),
                                };
                            })}
                        />
                    </Form.Item>
                    <Form.Item
                        name='userAccountId'
                        label='Счет пополнения'
                        rules={[{ required: true, message: 'Укажите счет для пополнения' }]}
                        initialValue={transaction?.userAccountId}
                    >
                        <Select
                            disabled={!!transaction}
                            onChange={(value: string) => {
                                setCurrentUserAccount(toAccounts.find((a) => a.id == value));
                            }}
                            options={toAccounts.map((a) => {
                                return {
                                    value: a.id,
                                    label: (
                                        <Flex align='center' gap='small'>
                                            <Account type={a.type} currency={a.currency} userBalanceType={a.userBalanceType} />
                                            <span style={{}}>{toFinanceString(a.amount || 0, 2)}</span>
                                        </Flex>
                                    ),
                                };
                            })}
                        />
                    </Form.Item>
                    <Form.Item label='Сумма' wrapperCol={{ span: 24 }}>
                        <Flex>
                            <Form.Item
                                name='amount'
                                wrapperCol={{ span: 6 }}
                                rules={[{ required: true, message: 'Укажите сумму' }]}
                                initialValue={transaction?.fromAmount}
                            >
                                <InputNumber
                                    disabled={!!transaction}
                                    precision={2}
                                    decimalSeparator=','
                                    min={0}
                                    style={{ width: '170px' }}
                                    onChange={(value: number | null) => {
                                        form.setFieldValue('amount', value == null ? undefined : value);
                                    }}
                                />
                            </Form.Item>
                            {!transaction && currentFromUserAccount && (
                                <Button
                                    style={{ color: '#1677ff', fontSize: 16 }}
                                    variant='link'
                                    color='blue'
                                    onClick={() => {
                                        form.setFieldValue('amount', currentFromUserAccount.amount);
                                    }}
                                >
                                    {toFinanceString(currentFromUserAccount.amount, 2)}
                                    {currencySign(currentFromUserAccount.currency)}
                                </Button>
                            )}
                        </Flex>
                    </Form.Item>
                    {currentUserAccount && currentFromUserAccount?.currency == Currency.Rub && (
                        <Form.Item
                            initialValue={currentRateType}
                            label='Источник курса'
                            name='exchangeRateType'
                            rules={[{ required: true, message: 'Укажите источник курса' }]}
                        >
                            <Select
                                onChange={(value: ExchangeRateType) => {
                                    setCurrentRateType(value);
                                }}
                                options={rateTypes.map((a) => {
                                    return { value: a.value, label: a.label };
                                })}
                            />
                        </Form.Item>
                    )}

                    {currentUserAccount && currentFromUserAccount && currentFromUserAccount.currency !== currentUserAccount.currency && (
                        <Form.Item label='Курс'>
                            <Flex align='start' gap='middle'>
                                {currentRateType == ExchangeRateType.CentralBank && (
                                    <Form.Item
                                        name='exchangeRateDate'
                                        rules={[{ required: true, message: 'Укажите дату курса' }]}
                                        style={{ width: '50%', marginBottom: 0 }}
                                    >
                                        <DatePicker
                                            format={'DD.MM.YYYY'}
                                            onChange={(data) => {
                                                form.setFieldValue('exchangeRate', undefined);
                                                form.setFieldValue(
                                                    'exchangeRateDate',
                                                    data ? dayjs(data).utc(true).startOf('day') : undefined,
                                                );
                                            }}
                                        />
                                    </Form.Item>
                                )}

                                <Form.Item name='exchangeRate' rules={[{ required: true, message: 'Укажите курс' }]}>
                                    <InputNumber
                                        decimalSeparator=','
                                        precision={4}
                                        style={{ width: 100 }}
                                        min={0}
                                        onChange={(value: number | null) => {
                                            form.setFieldValue('exchangeRate', value);
                                        }}
                                    />
                                </Form.Item>

                                <PlusOutlined style={{ height: 30 }} />

                                <Form.Item>
                                    <Space.Compact>
                                        <Form.Item name='exchangeRateCommission' noStyle initialValue={transaction?.exchangeRateCommission}>
                                            <InputNumber
                                                precision={2}
                                                decimalSeparator=','
                                                min={0}
                                                style={{ width: 100 }}
                                                onChange={(value: number | null) => {
                                                    form.setFieldValue('exchangeRateCommission', value);
                                                }}
                                            />
                                        </Form.Item>
                                        <Select
                                            style={{ width: 70 }}
                                            defaultValue={
                                                transaction?.exchangeRateCommissionType ||
                                                user?.userSettings?.exchangeRateCommissionType ||
                                                ExchangeRateCommissionType.Percentage
                                            }
                                            disabled={!currentUserAccount}
                                            onChange={(value: ExchangeRateCommissionType) => {
                                                form.setFieldValue('exchangeRateCommissionType', value);
                                            }}
                                            options={rateCommissionTypes.map((c) => {
                                                return {
                                                    value: c.value,
                                                    label:
                                                        c.value == ExchangeRateCommissionType.Percentage
                                                            ? '%'
                                                            : currencySign(currentFromUserAccount?.currency),
                                                };
                                            })}
                                        />
                                    </Space.Compact>
                                </Form.Item>
                            </Flex>
                        </Form.Item>
                    )}

                    <Form.Item name='resultAmount' label='ИТОГО'>
                        <InputNumber
                            decimalSeparator=','
                            disabled={true}
                            precision={2}
                            min={0}
                            style={{ width: '170px', background: '#f6fefa' }}
                        />
                    </Form.Item>
                </Form>
            )}
        </Modal>
    );
};

export default TransferAccountsModal;
